import { useState, useCallback, useMemo, useEffect } from "react";
import {
  CON_DEFAULT_VARIANT_VALUE,
  CON_DEFAULT_VARIANT_DESC,
  CON_DEFAULT_VARIANT_CODE,
  CON_MAX_VARIANT_COMBINATIONS,
} from "../utils/sys/Constants.tsx";

// Types and Interfaces
export interface Variant {
  type: string;
  values: string[];
}

export interface VariantTypeOption {
  code: number | string;
  label: string;
  value: string;
}

export interface ApiVariantRow {
  VARIANTCODE?: number | string;
  variantcode?: number | string;
  VARIANTNAME?: string;
  variantname?: string;
  VARIANTDATATYPE?: string;
  variantdatatype?: string;
  [key: string]: any;
}

export interface VariantDataItem {
  variantcode?: number | string;
  variantname?: string;
  variantName?: string;
  variantvalue?: string;
  variantValue?: string;
  type?: string;
  values?: string[];
  [key: string]: any;
}

export interface VariantPayload {
  itemcode: string | number;
  variantcode: number;
  variantvalue: string;
  descrip: string;
  itemimage: string;
}

export interface AddVariantResult {
  success: boolean;
  message?: string;
}

export interface UseVariantsReturn {
  variants: Variant[];
  variantTypes: VariantTypeOption[];
  newVariantName: string;
  newVariantValue: string;
  setVariants: React.Dispatch<React.SetStateAction<Variant[]>>;
  setNewVariantName: React.Dispatch<React.SetStateAction<string>>;
  setNewVariantValue: React.Dispatch<React.SetStateAction<string>>;
  parseVariantData: (variantInput: string | any[] | null | undefined) => void;
  handleAddNewVariant: () => AddVariantResult;
  handleRemoveVariantValue: (variantIndex: number, valueIndex: number) => void;
  handleRemoveVariant: (variantIndex: number) => void;
  canRemoveVariantValue: (variantIndex: number, valueIndex: number) => boolean;
  canRemoveVariantType: (variantIndex: number) => boolean;
  handleAddVariantType: (variantName: string, variantValue: string) => void;
  formatVariantData: () => string;
  prepareVariantPayload: (itemCode: string | number) => VariantPayload[];
  combinationCount: number;
  combinations: Record<string, string>[];
  generateVariantCombinations: (variantList: Variant[]) => Record<string, string>[];
  getCombinationCount: (variantList: Variant[]) => number;
}

const makeVariantValueKey = (type: string, value: string): string =>
  `${(type || "").toString().trim().toLowerCase()}::${(value || "")
    .toString()
    .trim()
    .toLowerCase()}`;

const buildLockedVariantValueSet = (variantArray: Variant[]): Set<string> => {
  const locked = new Set<string>();
  (variantArray || []).forEach((variant) => {
    (variant.values || []).forEach((value) => {
      locked.add(makeVariantValueKey(variant.type, value));
    });
  });
  return locked;
};

/**
 * Map API row (VARIANTCODE, VARIANTNAME, VARIANTDATATYPE) to dropdown option { code, label, value }.
 */
export const mapVariantTypeFromApi = (row: ApiVariantRow): VariantTypeOption => ({
  code: row.VARIANTCODE ?? row.variantcode ?? 0,
  label: (row.VARIANTNAME ?? row.variantname ?? "").trim() || "",
  value: (row.VARIANTNAME ?? row.variantname ?? "").trim() || "",
});

/**
 * @param {boolean} [singleValuePerType=false] - When true (edit mode), each variant type can have only one value (one combination). When false (create), multiple values per type allowed.
 * @param {Array<{ code, label, value }>} [initialVariantTypes] - Variant types from Getitemvarianttypes API (mapped). When provided, used as dropdown options.
 */
export const useVariants = (
  singleValuePerType: boolean = false,
  initialVariantTypes: VariantTypeOption[] | null = null
): UseVariantsReturn => {
  const [variants, setVariants] = useState<Variant[]>([]);
  const [variantTypes, setVariantTypes] = useState<VariantTypeOption[]>([]);
  const [lockedVariantValueKeys, setLockedVariantValueKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!initialVariantTypes || !Array.isArray(initialVariantTypes)) return;
    const fromApi = initialVariantTypes.filter((t) => t && (t.label || t.value));
    if (fromApi.length === 0) return;
    setVariantTypes((prev: VariantTypeOption[]) => {
      const apiCodes = new Set(fromApi.map((t) => t.code));
      const apiLabels = new Set(
        fromApi.map((t) => (t.label || t.value || "").toString().trim().toLowerCase())
      );
      const addedThisSession = prev.filter(
        (t) =>
          !apiCodes.has(t.code) &&
          !apiLabels.has((t.label || t.value || "").toString().trim().toLowerCase())
      );
      return [...fromApi, ...addedThisSession];
    });
  }, [initialVariantTypes]);
  
  const [newVariantName, setNewVariantName] = useState<string>("");
  const [newVariantValue, setNewVariantValue] = useState<string>("");

  const parseVariantData = useCallback(
    (variantInput: string | any[] | null | undefined): void => {
      if (variantInput === undefined || variantInput === null || variantInput === "") {
        setVariants([]);
        setLockedVariantValueKeys(new Set());
        return;
      }

      const applyParsedVariants = (variantArray: Variant[]): void => {
        setVariants(variantArray);
        if (singleValuePerType) {
          setLockedVariantValueKeys(buildLockedVariantValueSet(variantArray));
        } else {
          setLockedVariantValueKeys(new Set());
        }
      };

      try {
        if (Array.isArray(variantInput)) {
          const variantArray: Variant[] = variantInput
            .map((variant: any) => {
              const type = (variant?.type || variant?.variantname || "").toString().trim();
              const values = Array.isArray(variant?.values)
                ? variant.values
                    .map((value: any) => (value || "").toString().trim())
                    .filter(Boolean)
                : [];
              if (!type) return null;
              return { type, values };
            })
            .filter(Boolean) as Variant[];
          applyParsedVariants(variantArray);
          return;
        }

        const str =
          typeof variantInput === "string" ? variantInput.trim() : String(variantInput);

        // API format: JSON string array e.g. "[{\"variantcode\":1,\"variantname\":\"Size\",\"variantvalue\":\"3\"},...]"
        if (str.startsWith("[")) {
          let parsed: VariantDataItem[] = [];
          try {
            let cleaned = str.replace(/\\"/g, '"');
            if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
              cleaned = cleaned.slice(1, -1);
            }
            parsed = JSON.parse(cleaned);
          } catch (e) {
            parsed = JSON.parse(str);
          }
          const arr = Array.isArray(parsed) ? parsed : [];
          const byName: Record<string, string[]> = {};
          arr.forEach((v: VariantDataItem) => {
            const name = (v.variantname || v.variantName || "").trim();
            const val = (v.variantvalue || v.variantValue || "").trim();
            if (!name) return;
            if (!byName[name]) byName[name] = [];
            if (val && !byName[name].includes(val)) byName[name].push(val);
          });
          const variantArray: Variant[] = Object.entries(byName).map(([type, values]) => ({
            type,
            values,
          }));
          applyParsedVariants(variantArray);
          return;
        }

        // Legacy format: "Type1: val1, val2; Type2: val3"
        const variantArray: Variant[] = [];
        const variantEntries = str
          .split(";")
          .map((v) => v.trim())
          .filter(Boolean);
        variantEntries.forEach((entry) => {
          const [type, valuesStr] = entry.split(":").map((s) => s.trim());
          if (type && valuesStr) {
            const values = valuesStr
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean);
            variantArray.push({ type, values });
          }
        });
        applyParsedVariants(variantArray);
      } catch (error) {
        console.error("Error parsing variant data:", error);
        setVariants([]);
        setLockedVariantValueKeys(new Set());
      }
    },
    [singleValuePerType]
  );

  // Calculate combination count
  const getCombinationCount = useCallback((variantList: Variant[]): number => {
    if (!variantList || variantList.length === 0) return 1;
    return variantList.reduce((total: number, variant: Variant) => {
      const valueCount = variant.values && variant.values.length > 0 ? variant.values.length : 1;
      return total * valueCount;
    }, 1);
  }, []);

  // Generate all variant combinations (cartesian product)
  const generateVariantCombinations = useCallback((variantList: Variant[]): Record<string, string>[] => {
    if (!variantList || variantList.length === 0) return [{}];

    const generate = (index: number, currentCombo: Record<string, string>): Record<string, string>[] => {
      if (index === variantList.length) {
        return [currentCombo];
      }

      const variant = variantList[index];
      const results: Record<string, string>[] = [];

      if (variant.values && variant.values.length > 0) {
        for (const value of variant.values) {
          const newCombo = {
            ...currentCombo,
            [variant.type]: value,
          };
          results.push(...generate(index + 1, newCombo));
        }
      } else {
        results.push(...generate(index + 1, currentCombo));
      }

      return results;
    };

    return generate(0, {});
  }, []);

  // Memoized combination count
  const combinationCount = useMemo(() => getCombinationCount(variants), [variants, getCombinationCount]);

  // Memoized combinations
  const combinations = useMemo(() => generateVariantCombinations(variants), [variants, generateVariantCombinations]);

  const handleAddNewVariant = useCallback((): AddVariantResult => {
    if (!newVariantName.trim() || !newVariantValue.trim()) {
      return { success: false, message: "Please enter both variant name and value" };
    }

    const existingVariant = variants.find((v) => v.type === newVariantName);
    if (existingVariant) {
      // When editing: each variant type can have only one value (one combination per item)
      if (singleValuePerType && existingVariant.values.length >= 1) {
        return {
          success: false,
          message: "Each variant type can have only one value (one combination per item).",
        };
      }
      if (existingVariant.values.includes(newVariantValue.trim())) {
        return { success: false, message: `Value "${newVariantValue.trim()}" already exists for ${newVariantName}` };
      }

      const updatedVariant = {
        ...existingVariant,
        values: [...existingVariant.values, newVariantValue.trim()],
      };
      const updatedVariants = variants.map((v) =>
        v.type === newVariantName ? updatedVariant : v
      );
      const newCombinationCount = getCombinationCount(updatedVariants);

      if (newCombinationCount > CON_MAX_VARIANT_COMBINATIONS) {
        return {
          success: false,
          message: `Adding this value would create ${newCombinationCount} combinations, exceeding the maximum limit of ${CON_MAX_VARIANT_COMBINATIONS}`,
        };
      }

      setVariants(updatedVariants);
    } else {
      // Calculate new combination count if we add this new variant
      const updatedVariants = [
        ...variants,
        {
          type: newVariantName,
          values: [newVariantValue.trim()],
        },
      ];
      const newCombinationCount = getCombinationCount(updatedVariants);

      // Check 64-combination limit
      if (newCombinationCount > CON_MAX_VARIANT_COMBINATIONS) {
        return {
          success: false,
          message: `Adding this variant would create ${newCombinationCount} combinations, exceeding the maximum limit of ${CON_MAX_VARIANT_COMBINATIONS}`,
        };
      }

      setVariants(updatedVariants);
    }

    setNewVariantName("");
    setNewVariantValue("");
    return { success: true };
  }, [newVariantName, newVariantValue, variants, getCombinationCount, singleValuePerType]);

  const handleRemoveVariantValue = useCallback(
    (variantIndex: number, valueIndex: number): void => {
      setVariants((prev: Variant[]) =>
        prev.map((variant, idx) => {
          if (idx !== variantIndex) return variant;
          const valueToRemove = variant.values?.[valueIndex];
          if (
            singleValuePerType &&
            valueToRemove &&
            lockedVariantValueKeys.has(
              makeVariantValueKey(variant.type, valueToRemove)
            )
          ) {
            return variant;
          }
          const newValues = [...variant.values];
          newValues.splice(valueIndex, 1);
          return { ...variant, values: newValues };
        })
      );
    },
    [singleValuePerType, lockedVariantValueKeys]
  );

  const handleRemoveVariant = useCallback(
    (variantIndex: number): void => {
      setVariants((prev: Variant[]) => {
        const variant = prev[variantIndex];
        if (!variant) return prev;
        if (
          singleValuePerType &&
          (variant.values || []).some((value) =>
            lockedVariantValueKeys.has(makeVariantValueKey(variant.type, value))
          )
        ) {
          return prev;
        }
        return prev.filter((_, idx) => idx !== variantIndex);
      });
    },
    [singleValuePerType, lockedVariantValueKeys]
  );

  const canRemoveVariantValue = useCallback(
    (variantIndex: number, valueIndex: number): boolean => {
      if (!singleValuePerType) return true;
      const variant = variants[variantIndex];
      if (!variant) return false;
      const value = variant.values?.[valueIndex];
      return !lockedVariantValueKeys.has(makeVariantValueKey(variant.type, value || ""));
    },
    [singleValuePerType, variants, lockedVariantValueKeys]
  );

  const canRemoveVariantType = useCallback(
    (variantIndex: number): boolean => {
      if (!singleValuePerType) return true;
      const variant = variants[variantIndex];
      if (!variant) return false;
      return !(variant.values || []).some((value) =>
        lockedVariantValueKeys.has(makeVariantValueKey(variant.type, value))
      );
    },
    [singleValuePerType, variants, lockedVariantValueKeys]
  );

  const handleAddVariantType = useCallback((variantName: string, variantValue: string): void => {
    setVariantTypes((prev: VariantTypeOption[]) => [
      ...prev,
      {
        label: variantName,
        value: variantValue.toLowerCase(),
        code: prev.length + 1,
      },
    ]);
  }, []);

  const formatVariantData = useCallback((): string => {
    return variants.map((v: Variant) => `${v.type}: ${v.values.join(", ")}`).join("; ");
  }, [variants]);

  const prepareVariantPayload = useCallback((itemCode: string | number): VariantPayload[] => {
    const productItemvariants: VariantPayload[] = [];

    variants.forEach((variant: Variant, variantIndex: number) => {
      variant.values.forEach((value: string) => {
        productItemvariants.push({
          itemcode: itemCode,
          variantcode: variantIndex + 1,
          variantvalue: value,
          descrip: `${variant.type}: ${value}`,
          itemimage: "",
        });
      });
    });

    // If no variants, add a default entry
    if (productItemvariants.length === 0) {
      productItemvariants.push({
        itemcode: itemCode,
        variantcode: CON_DEFAULT_VARIANT_CODE,
        variantvalue: CON_DEFAULT_VARIANT_VALUE,
        descrip: CON_DEFAULT_VARIANT_DESC,
        itemimage: "",
      });
    }

    return productItemvariants;
  }, [variants]);

  return {
    variants,
    variantTypes,
    newVariantName,
    newVariantValue,
    setVariants,
    setNewVariantName,
    setNewVariantValue,
    parseVariantData,
    handleAddNewVariant,
    handleRemoveVariantValue,
    handleRemoveVariant,
    canRemoveVariantValue,
    canRemoveVariantType,
    handleAddVariantType,
    formatVariantData,
    prepareVariantPayload,
    combinationCount,
    combinations,
    generateVariantCombinations,
    getCombinationCount,
  };
};